// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, extendTheme, Spinner, Center } from '@chakra-ui/react';
import { createClient } from '@supabase/supabase-js';

// Componentes de Autenticação
import LoginComPerfil from './components/auth/LoginComPerfil';

// Componentes Principais
import Home from './components/home/Home';
import EventosMain from './components/eventos/EventosMain';
import UsuariosMain from './components/usuarios/UsuariosMain';
import ClientesMain from './components/clientes/ClientesMain';
import PerfilUsuario from './components/perfil/PerfilUsuario';
import EventoPublico from './components/eventos/views/EventoPublico';
import EventoAoVivo from './components/eventos/views/EventoAoVivo';
import EventoTV from './components/eventos/views/EventoTV';
import LandingPage from './components/landing/LandingPage';

// Flags futuras do React Router v6 (elimina warnings de upgrade para v7)
const routerFutureConfig = { v7_startTransition: true, v7_relativeSplatPath: true };

// Configuração do Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// Tema customizado com as cores da marca
const theme = extendTheme({
    colors: {
        brand: {
            primary: '#0E2C52',
            gradientStart: '#2CCB90',
            gradientEnd: '#2DB8E1',
            50: '#E6F0FF',
            100: '#B8D4F0',
            500: '#0E2C52',
            600: '#0A1F3A',
            700: '#071528',
        },
    },
    fonts: {
        heading: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
        body: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
    },
    styles: {
        global: {
            body: {
                bg: 'white',
                color: 'gray.800',
            },
        },
    },
});

function App() {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [clienteData, setClienteData] = useState(null);
    const [userPerfil, setUserPerfil] = useState(null);

    useEffect(() => {
        // Verificar sessão ativa
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            if (session) {
                loadUserData(session);
            }
            setLoading(false);
        });

        // Escutar mudanças na autenticação
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            if (session) {
                loadUserData(session);
            } else {
                setClienteData(null);
                setUserPerfil(null);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const loadUserData = async (session) => {
        try {
            // Se app_metadata não tiver perfil (sessão antiga), definir agora
            if (!session.user.app_metadata?.perfil) {
                await supabase.rpc('definir_metadata_auth');
                const { data: { session: refreshed } } = await supabase.auth.refreshSession();
                if (refreshed) { loadUserData(refreshed); return; }
            }

            // Obter perfil do usuário dos metadados
            const perfil = session.user.user_metadata?.perfil || session.user.app_metadata?.perfil || null;
            setUserPerfil(perfil);

            // Se for super_admin, não precisa de cliente
            if (perfil === 'super_admin') {
                setClienteData(null);
                return;
            }

            // Obter cliente_id dos metadados
            let clienteId = session.user.user_metadata?.cliente_id || session.user.app_metadata?.cliente_id;

            // Se não tiver nos metadados, tentar pegar da tabela de usuários do sistema
            if (!clienteId) {
                const emailReal = session.user.user_metadata?.email_real || session.user.email;

                const { data: usuarioSistema, error: errorUsuario } = await supabase
                    .from('usuarios_sistema')
                    .select('cliente_id, perfil')
                    .eq('email', emailReal)
                    .single();

                if (!errorUsuario && usuarioSistema) {
                    clienteId = usuarioSistema.cliente_id;
                    setUserPerfil(usuarioSistema.perfil);
                }
            }

            if (clienteId) {
                // Buscar dados do cliente
                const { data, error } = await supabase
                    .from('clientes')
                    .select('*')
                    .eq('id', clienteId)
                    .single();

                if (error) throw error;
                setClienteData(data);
            }
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
        }
    };

    const handleLoginSuccess = (loginData) => {
        console.log('Login bem-sucedido:', loginData);
        // Forçar atualização da sessão caso onAuthStateChange demore
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                setSession(session);
                loadUserData(session);
            }
        });
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setSession(null);
        setClienteData(null);
        setUserPerfil(null);
    };

    if (loading) {
        return (
            <ChakraProvider theme={theme}>
                <Center h="100vh" bg="white">
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="brand.gradientStart"
                        size="xl"
                    />
                </Center>
            </ChakraProvider>
        );
    }

    return (
        <ChakraProvider theme={theme}>
            <Router future={routerFutureConfig}>
                <Routes>
                    {/* Rota de Login */}
                    <Route
                        path="/login"
                        element={
                            !session ? (
                                <LoginComPerfil
                                    supabase={supabase}
                                    onLoginSuccess={handleLoginSuccess}
                                />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />

                    {/* Rota Principal - Home (logado) ou Landing Page (não logado) */}
                    <Route
                        path="/"
                        element={
                            session ? (
                                <Home
                                    supabase={supabase}
                                    session={session}
                                    clienteData={clienteData}
                                    onLogout={handleLogout}
                                />
                            ) : (
                                <LandingPage />
                            )
                        }
                    />

                    {/* Rota de Gerenciamento de Clientes - APENAS SUPER_ADMIN */}
                    <Route
                        path="/clientes"
                        element={
                            session ? (
                                userPerfil === 'super_admin' ? (
                                    <ClientesMain
                                        supabase={supabase}
                                        session={session}
                                        onLogout={handleLogout}
                                    />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />

                    {/* Rota de Eventos - Todas as sub-rotas */}
                    <Route
                        path="/eventos/*"
                        element={
                            session ? (
                                <EventosMain
                                    supabase={supabase}
                                    session={session}
                                    clienteData={clienteData}
                                    onLogout={handleLogout}
                                />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />

                    {/* Rota de Gerenciamento de Usuários - APENAS ADMIN (não super_admin) */}
                    <Route
                        path="/usuarios"
                        element={
                            session ? (
                                userPerfil === 'admin' ? (
                                    <UsuariosMain
                                        supabase={supabase}
                                        session={session}
                                        clienteData={clienteData}
                                        onLogout={handleLogout}
                                    />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />

                    {/* Rota de Perfil do Usuário - TODOS OS PERFIS */}
                    <Route
                        path="/perfil"
                        element={
                            session ? (
                                <PerfilUsuario
                                    supabase={supabase}
                                    session={session}
                                    clienteData={clienteData}
                                    onLogout={handleLogout}
                                />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />

                    {/* Rota pública - Programação do evento específico (sem login) */}
                    <Route
                        path="/evento/:id"
                        element={<EventoPublico supabase={supabase} />}
                    />

                    {/* Rota pública permanente por cliente - sempre exibe evento ao vivo */}
                    <Route
                        path="/ao-vivo/:clienteId"
                        element={<EventoAoVivo supabase={supabase} />}
                    />

                    {/* Rota pública TV — layout fullscreen para telão */}
                    <Route
                        path="/tv/:clienteId"
                        element={<EventoTV supabase={supabase} />}
                    />

                    {/* Rota catch-all - Redirecionar para home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>
        </ChakraProvider>
    );
}

export default App;
